import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import {
  Modal, Field, Input, Textarea, BtnPrimary, BtnSecondary,
} from "@/components/primitives";
import { PaperPlaneTilt, X } from "@phosphor-icons/react";

/**
 * Generic email send dialog.
 *
 * Props:
 *   open, onClose
 *   title           — dialog title
 *   endpoint        — POST endpoint that accepts { to, cc, subject, body }
 *   initialTo       — string (one address) or array — pre-fill recipients
 *   initialSubject  — string — pre-fill subject (admin can edit)
 *   bodyPreview     — string — shown in a read-only preview pane to give context
 *   masterCcHint    — string — the master CC that will be auto-appended (display only)
 *   onSent()        — called on success
 */
export default function SendEmailDialog({
  open, onClose, title = "Send Email",
  endpoint, initialTo = "", initialSubject = "",
  bodyPreview = "", masterCcHint = "",
  onSent,
}) {
  const [toList, setToList] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [toDraft, setToDraft] = useState("");
  const [ccDraft, setCcDraft] = useState("");
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const ini = Array.isArray(initialTo) ? initialTo : (initialTo ? [initialTo] : []);
    setToList(ini.filter(Boolean));
    setCcList([]);
    setToDraft("");
    setCcDraft("");
    setSubject(initialSubject);
    setNote("");
  }, [open, initialTo, initialSubject]);

  function addChip(setList, list, draft, setDraft) {
    const val = draft.trim().replace(/[,;\s]+$/g, "");
    if (!val) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      toast.error(`"${val}" doesn't look like a valid email`);
      return;
    }
    if (list.includes(val)) { setDraft(""); return; }
    setList([...list, val]);
    setDraft("");
  }
  function removeChip(setList, list, val) {
    setList(list.filter((x) => x !== val));
  }
  function onKey(setList, list, draft, setDraft) {
    return (e) => {
      if (["Enter", ",", " ", ";"].includes(e.key)) {
        e.preventDefault();
        addChip(setList, list, draft, setDraft);
      } else if (e.key === "Backspace" && !draft && list.length) {
        setList(list.slice(0, -1));
      }
    };
  }

  async function send() {
    // Flush any half-typed addresses
    const toFinal = toDraft.trim() ? [...toList, toDraft.trim()] : toList;
    const ccFinal = ccDraft.trim() ? [...ccList, ccDraft.trim()] : ccList;
    if (!toFinal.length) { toast.error("Add at least one recipient"); return; }
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    setSending(true);
    try {
      const { data } = await api.post(endpoint, {
        to: toFinal, cc: ccFinal,
        subject: subject.trim(),
        body: note,   // optional note prepended on backend in future, today server uses template
        body_is_html: false,
      });
      toast.success(`Email sent to ${data.to.join(", ")}`);
      onSent?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={title} wide testId="send-email-dialog">
      <div className="space-y-4">
        {/* TO chips */}
        <Field label="To (one or more emails — press Enter or comma to add)">
          <div className="border border-[#e4e4e7] bg-white px-2 py-1.5 min-h-[42px] flex flex-wrap gap-1.5 items-center focus-within:border-[#002FA7]">
            {toList.map((e) => (
              <span key={e} className="bg-[#f4f4f5] border border-[#e4e4e7] px-2 py-0.5 text-xs mono flex items-center gap-1">
                {e}
                <button onClick={() => removeChip(setToList, toList, e)} className="text-[#a1a1aa] hover:text-[#FF2A2A]" type="button">
                  <X size={10} weight="bold" />
                </button>
              </span>
            ))}
            <input
              data-testid="email-to-input"
              className="flex-1 min-w-[180px] outline-none text-sm px-1 py-1"
              value={toDraft}
              onChange={(e) => setToDraft(e.target.value)}
              onKeyDown={onKey(setToList, toList, toDraft, setToDraft)}
              onBlur={() => toDraft && addChip(setToList, toList, toDraft, setToDraft)}
              placeholder={toList.length ? "" : "client@example.com"}
              type="email"
            />
          </div>
        </Field>

        {/* CC chips */}
        <Field label="CC (optional)" hint={masterCcHint ? `Master ${masterCcHint} will always be CC'd automatically.` : null}>
          <div className="border border-[#e4e4e7] bg-white px-2 py-1.5 min-h-[42px] flex flex-wrap gap-1.5 items-center focus-within:border-[#002FA7]">
            {ccList.map((e) => (
              <span key={e} className="bg-[#f4f4f5] border border-[#e4e4e7] px-2 py-0.5 text-xs mono flex items-center gap-1">
                {e}
                <button onClick={() => removeChip(setCcList, ccList, e)} className="text-[#a1a1aa] hover:text-[#FF2A2A]" type="button">
                  <X size={10} weight="bold" />
                </button>
              </span>
            ))}
            <input
              data-testid="email-cc-input"
              className="flex-1 min-w-[180px] outline-none text-sm px-1 py-1"
              value={ccDraft}
              onChange={(e) => setCcDraft(e.target.value)}
              onKeyDown={onKey(setCcList, ccList, ccDraft, setCcDraft)}
              onBlur={() => ccDraft && addChip(setCcList, ccList, ccDraft, setCcDraft)}
              placeholder=""
              type="email"
            />
          </div>
        </Field>

        <Field label="Subject">
          <Input
            data-testid="email-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
          />
        </Field>

        {bodyPreview ? (
          <Field label="Body preview (auto-generated from the record)">
            <div className="border border-[#e4e4e7] bg-[#fafafa] px-3 py-2 text-xs mono whitespace-pre-wrap max-h-44 overflow-auto text-[#52525b]">
              {bodyPreview}
            </div>
          </Field>
        ) : null}

        <Field label="Add a personal note (optional — currently informational)">
          <Textarea
            data-testid="email-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Hi team, quick note before the auto-generated body..."
          />
        </Field>

        <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
          <BtnSecondary type="button" onClick={onClose}>Cancel</BtnSecondary>
          <BtnPrimary type="button" onClick={send} disabled={sending} data-testid="email-send-btn">
            <PaperPlaneTilt size={14} weight="bold" className="inline mr-1.5" />
            {sending ? "Sending…" : "Send Email"}
          </BtnPrimary>
        </div>
      </div>
    </Modal>
  );
}
